import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  clearMoodError,
  createMoodEntry,
  fetchMoodEntriesByDate,
  selectMoodEntriesForDate,
  selectMoodError,
  selectMoodLoadingForDate,
} from '../../redux/mood/moodSlice'
import { todayKey } from '../../redux/tasks'
import type { MoodEntryDto } from '../../Models/Mood'
import { TagInput } from '../TagInput'
import styles from './MoodSection.module.css'

export type MoodSectionProps = {
  dateKey: string
  /** When set, only the "log today" form is shown (no list or per-day fetch). */
  variant?: 'default' | 'formOnly'
}

const SCORE_MIN = 1
const SCORE_MAX = 10

/** Auto-label based on hour of the log */
export const getCheckInTitle = (iso: string): string => {
  const h = new Date(iso).getHours()
  if (h >= 5 && h < 12) return 'Morning Check-in'
  if (h >= 12 && h < 17) return 'Afternoon Check-in'
  if (h >= 17 && h < 21) return 'Evening Check-in'
  return 'Night Check-in'
}

export const formatLoggedTime = (iso: string): string => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

/** Score → emoji face SVG */
export const MoodScoreFace = ({ score }: { score: number }) => {
  if (score <= 3) {
    return (
      <svg width="20" height="20" viewBox="0 0 32 32" fill="none" aria-hidden>
        <circle cx="16" cy="16" r="16" fill="transparent" />
        <circle cx="11.5" cy="13.5" r="2" fill="currentColor" />
        <circle cx="20.5" cy="13.5" r="2" fill="currentColor" />
        <path d="M10 22c1.5-2.5 10.5-2.5 12 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M11 10.5c.8-1 2-1.5 3-1.2M21 10.5c-.8-1-2-1.5-3-1.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }
  if (score <= 7) {
    return (
      <svg width="20" height="20" viewBox="0 0 32 32" fill="none" aria-hidden>
        <circle cx="16" cy="16" r="16" fill="transparent" />
        <circle cx="11.5" cy="13.5" r="2" fill="currentColor" />
        <circle cx="20.5" cy="13.5" r="2" fill="currentColor" />
        <path d="M10.5 21.5c1.5-2.5 9.5-2.5 11 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    )
  }
  return (
    <svg width="20" height="20" viewBox="0 0 32 32" fill="none" aria-hidden>
      <circle cx="16" cy="16" r="16" fill="transparent" />
      <circle cx="11.5" cy="13.5" r="2" fill="currentColor" />
      <circle cx="20.5" cy="13.5" r="2" fill="currentColor" />
      <path d="M10 18.5c1.5 3.5 10.5 3.5 12 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

const SCORE_SCALE = Array.from({ length: 10 }, (_, i) => String(i + 1))

// ── Logged Today card ──────────────────────────────────────────────────

const LoggedTodayCard = ({ entry: e, dateKey }: { entry: MoodEntryDto; dateKey: string }) => {
  const allTags = [...(e.positiveTags ?? []), ...(e.negativeTags ?? [])]

  return (
    <article className={styles.logCard}>
      <div className={styles.logCardHeader}>
        <div className={styles.logCardLeft}>
          <span className={styles.logCardEmoji}>
            <MoodScoreFace score={e.score} />
          </span>
          <div className={styles.logCardMeta}>
            <span className={styles.logCardTitle}>{getCheckInTitle(e.loggedAt)}</span>
            <span className={styles.logCardTime}>{formatLoggedTime(e.loggedAt)}</span>
          </div>
        </div>
        <span className={styles.logCardScore} aria-label={`Score ${e.score}`}>
          {e.score}
        </span>
      </div>

      {allTags.length > 0 && (
        <div className={styles.logCardTags}>
          {allTags.map((t, i) => (
            <span key={`${t}-${i}`} className={styles.logCardTag}>
              {t}
            </span>
          ))}
        </div>
      )}

      {e.notes ? (
        <p className={styles.logCardNotes}>{e.notes}</p>
      ) : null}
    </article>
  )
}

// ── MoodSection ────────────────────────────────────────────────────────

const MoodSection = ({ dateKey, variant = 'default' }: MoodSectionProps) => {
  const dispatch = useAppDispatch()
  const entries = useAppSelector(selectMoodEntriesForDate(dateKey))
  const loading = useAppSelector(selectMoodLoadingForDate(dateKey))
  const fetchStatus = useAppSelector((s) => s.mood.statusByDate[dateKey])
  const error = useAppSelector(selectMoodError)

  const today = todayKey()
  const isToday = dateKey === today
  const isFuture = dateKey > today
  const formOnly = variant === 'formOnly' && isToday

  const [score, setScore] = useState<number>(5)
  const [notes, setNotes] = useState('')
  const [positiveTags, setPositiveTags] = useState<string[]>([])
  const [negativeTags, setNegativeTags] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (formOnly) return
    void dispatch(fetchMoodEntriesByDate(dateKey))
  }, [dispatch, dateKey, formOnly])

  useEffect(() => {
    if (!isToday) {
      setNotes('')
      setPositiveTags([])
      setNegativeTags([])
      setScore(5)
    }
  }, [isToday, dateKey])

  const sortedEntries = useMemo(
    () =>
      [...entries].sort(
        (a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()
      ),
    [entries]
  )

  const onSubmitNew = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!isToday || isFuture) return
    setSaving(true)
    try {
      await dispatch(
        createMoodEntry({
          dateKey,
          body: {
            score,
            notes: notes.trim() || undefined,
            positiveTags: positiveTags.length ? positiveTags : undefined,
            negativeTags: negativeTags.length ? negativeTags : undefined,
          },
        })
      ).unwrap()
      setNotes('')
      setPositiveTags([])
      setNegativeTags([])
      setScore(5)
    } catch {
      /* error in store */
    } finally {
      setSaving(false)
    }
  }

  const activeScale = Math.max(0, Math.min(9, score - 1))
  const hasTodayEntries = isToday && !loading && fetchStatus === 'succeeded' && sortedEntries.length > 0

  return (
    <div className={`${styles.root} ${hasTodayEntries && !formOnly ? styles.rootTwoCol : ''}`}>
      {/* ── Error banner ── */}
      {error ? (
        <p className={styles.errorBanner} role="alert">
          {error}{' '}
          <button
            type="button"
            className={styles.dismissError}
            onClick={() => dispatch(clearMoodError())}
          >
            Dismiss
          </button>
        </p>
      ) : null}

      {/* ── Left: form panel ── */}
      <div className={styles.panel} role="region" aria-label="Mood tracker">
        {!formOnly && loading ? (
          <div className={styles.skeleton} aria-hidden>
            <div className={styles.skeletonLineMed} />
            <div className={styles.skeletonLineShort} />
            <div className={styles.skeletonLine} />
          </div>
        ) : null}

        {!formOnly && !loading && isFuture ? (
          <p className={styles.hint}>
            You can log your mood when this day arrives. Pick today or an earlier day to see or add
            entries.
          </p>
        ) : null}

        {!loading && isToday ? (
          <form className={styles.form} onSubmit={onSubmitNew}>
            {/* Mood Slider */}
            <div className={styles.sliderSection}>
              <div className={styles.emojiRow}>
                {SCORE_SCALE.map((n, i) => (
                  <span
                    key={n}
                    className={`${styles.emojiIcon} ${activeScale === i ? styles.emojiActive : ''}`}
                  >
                    {n}
                  </span>
                ))}
              </div>

              <div className={styles.sliderWrapper}>
                <input
                  type="range"
                  min={SCORE_MIN}
                  max={SCORE_MAX}
                  value={score}
                  onChange={(e) => setScore(Number(e.target.value))}
                  className={styles.slider}
                  aria-label="Mood score"
                  aria-valuemin={SCORE_MIN}
                  aria-valuemax={SCORE_MAX}
                  aria-valuenow={score}
                />
              </div>

            </div>

            {/* What helped */}
            <div className={styles.fieldGroup}>
              <span className={styles.fieldLabel}>What helped</span>
              <div className={styles.tagInputWrapper}>
                <TagInput
                  id="mood-pos-tags"
                  tags={positiveTags}
                  onChange={setPositiveTags}
                  tone="positive"
                  disabled={saving}
                  placeholderEmpty="Add tag"
                  placeholderMore="Add tag"
                  aria-label="What helped"
                />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="mood-notes" className={styles.fieldLabel} />
              <textarea
                id="mood-notes"
                className={styles.textarea}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything on your mind..."
                rows={4}
              />
            </div>

            <div className={styles.fieldGroup}>
              <span className={styles.fieldLabel}>What was tough</span>
              <div className={styles.tagInputWrapper}>
                <TagInput
                  id="mood-neg-tags"
                  tags={negativeTags}
                  onChange={setNegativeTags}
                  tone="negative"
                  disabled={saving}
                  placeholderEmpty="Add tag"
                  placeholderMore="Add tag"
                  aria-label="What was tough"
                />
              </div>
            </div>

            <div className={styles.submitRow}>
              <button type="submit" className={styles.submitBtn} disabled={saving}>
                {saving ? 'Saving...' : 'Save mood'}
              </button>
            </div>
          </form>
        ) : null}

        {!formOnly && !loading && fetchStatus === 'failed' && !isToday ? (
          <p className={styles.hint}>
            Could not load mood for this day.{' '}
            <button
              type="button"
              className={styles.dismissError}
              onClick={() => void dispatch(fetchMoodEntriesByDate(dateKey))}
            >
              Try again
            </button>
          </p>
        ) : null}

        {/* Past-date entries (non-today) */}
        {!formOnly && !loading && fetchStatus === 'succeeded' && !isFuture && !isToday && sortedEntries.length > 0 ? (
          <div className={styles.entries}>
            <h3 className={styles.entriesTitle}>That day</h3>
            {sortedEntries.map((e) => (
              <LoggedTodayCard key={e.id} entry={e} dateKey={dateKey} />
            ))}
          </div>
        ) : null}

        {!formOnly && !loading && fetchStatus === 'succeeded' && !isFuture && !isToday && sortedEntries.length === 0 ? (
          <p className={styles.hint}>No mood logged for this day.</p>
        ) : null}
      </div>

      {/* ── Right: Logged Today panel ── */}
      {hasTodayEntries && !formOnly ? (
        <div className={styles.loggedTodayPanel}>
          <div className={styles.loggedTodayHeader}>
            <span className={styles.loggedTodayTitle}>Logged Today</span>
            <Link to="/mood" className={styles.viewAllLink}>View All</Link>
          </div>
          <div className={styles.loggedTodayList}>
            {sortedEntries.map((e) => (
              <LoggedTodayCard key={e.id} entry={e} dateKey={dateKey} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default MoodSection
